import type { Components } from 'react-markdown';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as React from 'react';

import { TableHeader, TableBody, TableHead, TableCell, TableRow, Table } from '../table/table';
import { typographyVariants } from '../typography/typography';
import { Typography } from '../typography/typography';
import { Anchor } from '../anchor/anchor';
import { cn } from '../../utils';

type TypographyVariant = NonNullable<Parameters<typeof typographyVariants>[0]>['variant'];

export type MarkdownProps = React.ComponentPropsWithoutRef<typeof ReactMarkdown> & {
  className?: string;
};

const headingVariants: Record<string, TypographyVariant> = {
  h1: 'h600',
  h2: 'h500',
  h3: 'h400',
  h4: 'h300',
  h5: 'h200',
  h6: 'h100',
};

export const Markdown = ({ children, className, ...props }: MarkdownProps) => {
  const components = {
    ...Object.entries(headingVariants).reduce(
      (acc, [level, typographyVariant]) => ({
        ...acc,
        [level]: ({ children, ...props }: React.ComponentPropsWithoutRef<'h1'>) => (
          <Typography
            element={level as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'}
            variant={typographyVariant}
            className={className}
            {...props}
          >
            {children}
          </Typography>
        ),
      }),
      {} as Components,
    ),
    ul: ({ children, ...props }: React.ComponentPropsWithoutRef<'ul'>) => (
      <ul className={cn('list-disc pl-6', className)} {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }: React.ComponentPropsWithoutRef<'ol'>) => (
      <ol className={cn('list-decimal pl-6', className)} {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }: React.ComponentPropsWithoutRef<'li'>) => (
      <Typography variant="paragraphBasicBook" className={className} element="li" {...props}>
        {children}
      </Typography>
    ),
    a: ({ children, href, ...props }: React.ComponentPropsWithoutRef<'a'>) => (
      <Anchor className={className} href={href} {...props}>
        {children}
      </Anchor>
    ),
    table: ({ children, ...props }: React.ComponentPropsWithoutRef<'table'>) => (
      <Table className={className} {...props}>
        {children}
      </Table>
    ),
    thead: ({ children, ...props }: React.ComponentPropsWithoutRef<'thead'>) => (
      <TableHeader className={className} {...props}>
        {children}
      </TableHeader>
    ),
    tbody: ({ children, ...props }: React.ComponentPropsWithoutRef<'tbody'>) => (
      <TableBody className={className} {...props}>
        {children}
      </TableBody>
    ),
    tr: ({ children, ...props }: React.ComponentPropsWithoutRef<'tr'>) => (
      <TableRow className={className} {...props}>
        {children}
      </TableRow>
    ),
    th: ({ children, ...props }: React.ComponentPropsWithoutRef<'th'>) => (
      <TableHead className={className} {...props}>
        {children}
      </TableHead>
    ),
    td: ({ children, ...props }: React.ComponentPropsWithoutRef<'td'>) => (
      <TableCell className={className} {...props}>
        {children}
      </TableCell>
    ),
  } as Components;

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} {...props}>
      {children}
    </ReactMarkdown>
  );
};
